import { apiFetch, apiUrl } from "@/lib/api";

export const ARCHIVE_TYPES = [
  "chat_history",
  "learning_record",
  "note",
  "persona",
  "question_bank",
  "skill",
] as const;

export type ArchiveType = (typeof ARCHIVE_TYPES)[number];

type JsonRecord = Record<string, unknown>;

export interface ArchiveAnchor {
  kind: "course" | "book" | "unclassified";
  /** English source string; rendered through i18next `t()`. */
  badge: "Course anchor" | "Book anchor" | "Uncategorized";
  ref?: JsonRecord;
}

export interface ArchiveItem {
  id: string;
  type: ArchiveType;
  title: string;
  meta: JsonRecord;
  snippet: string;
  when: string;
  anchor: ArchiveAnchor;
  target: JsonRecord;
}

export interface ArchiveResourceVersion {
  version: string;
  enabled: boolean;
  reason?: string;
}

export type ArchiveCounts = Record<ArchiveType, number | null>;

export interface ArchivePage {
  contractVersion: 1;
  snapshotAt: string;
  resourceVersions: Record<ArchiveType, ArchiveResourceVersion>;
  counts: ArchiveCounts;
  items: ArchiveItem[];
  page: {
    limit: number;
    nextCursor: string | null;
    hasMore: boolean;
  };
  partial: boolean;
  excludedTypes: ArchiveType[];
}

export interface ArchivePageRequest {
  type?: ArchiveType;
  q?: string;
  cursor?: string;
  limit?: number;
  knowledge?: string;
}

export class ArchiveApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null) {
    super(message);
    this.name = "ArchiveApiError";
    this.status = status;
    this.code = code;
  }
}

const archiveTypeSet = new Set<string>(ARCHIVE_TYPES);
const anchorBadges: Record<ArchiveAnchor["kind"], ArchiveAnchor["badge"]> = {
  course: "Course anchor",
  book: "Book anchor",
  unclassified: "Uncategorized",
};

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireRecord(value: unknown, name: string): JsonRecord {
  if (!isRecord(value)) throw new Error(`Invalid archive response: ${name}`);
  return value;
}

function requireString(value: unknown, name: string): string {
  if (typeof value !== "string") {
    throw new Error(`Invalid archive response: ${name}`);
  }
  return value;
}

function requireKeys(
  value: JsonRecord,
  expected: readonly string[],
  name: string,
): void {
  const keys = Object.keys(value);
  if (
    keys.length !== expected.length ||
    keys.some((key) => !expected.includes(key))
  ) {
    throw new Error(`Invalid archive response: ${name}`);
  }
}

function parseAnchor(value: unknown): ArchiveAnchor {
  const source = requireRecord(value, "item.anchor");
  const kind = requireString(source.kind, "item.anchor.kind");
  if (kind !== "course" && kind !== "book" && kind !== "unclassified") {
    throw new Error("Invalid archive response: item.anchor.kind");
  }
  if (source.badge !== anchorBadges[kind]) {
    throw new Error("Invalid archive response: item.anchor.badge");
  }

  if (kind === "unclassified") {
    if ("ref" in source) {
      throw new Error("Invalid archive response: item.anchor.ref");
    }
    return { kind, badge: anchorBadges[kind] };
  }

  return {
    kind,
    badge: anchorBadges[kind],
    ref: requireRecord(source.ref, "item.anchor.ref"),
  };
}

function parseItem(value: unknown): ArchiveItem {
  const source = requireRecord(value, "item");
  const type = requireString(source.type, "item.type");
  if (!archiveTypeSet.has(type)) {
    throw new Error("Invalid archive response: item.type");
  }
  const when = requireString(source.when, "item.when");
  if (!Number.isFinite(Date.parse(when))) {
    throw new Error("Invalid archive response: item.when");
  }

  return {
    id: requireString(source.id, "item.id"),
    type: type as ArchiveType,
    title: requireString(source.title, "item.title"),
    meta: requireRecord(source.meta, "item.meta"),
    snippet: requireString(source.snippet, "item.snippet"),
    when,
    anchor: parseAnchor(source.anchor),
    target: requireRecord(source.target, "item.target"),
  };
}

function parseResourceVersions(
  value: unknown,
  partial: boolean,
): Record<ArchiveType, ArchiveResourceVersion> {
  const source = requireRecord(value, "resource_versions");
  requireKeys(source, ARCHIVE_TYPES, "resource_versions");

  return Object.fromEntries(
    ARCHIVE_TYPES.map((type) => {
      const version = requireRecord(source[type], `resource_versions.${type}`);
      if (typeof version.enabled !== "boolean") {
        throw new Error(`Invalid archive response: resource_versions.${type}`);
      }
      if (partial ? type !== "persona" && !version.enabled : !version.enabled) {
        throw new Error(`Invalid archive response: resource_versions.${type}`);
      }
      return [
        type,
        {
          version: requireString(
            version.version,
            `resource_versions.${type}.version`,
          ),
          enabled: version.enabled,
          ...(typeof version.reason === "string" ? { reason: version.reason } : {}),
        },
      ];
    }),
  ) as Record<ArchiveType, ArchiveResourceVersion>;
}

function parseCounts(value: unknown, partial: boolean): ArchiveCounts {
  const source = requireRecord(value, "counts");
  requireKeys(source, ARCHIVE_TYPES, "counts");

  return Object.fromEntries(
    ARCHIVE_TYPES.map((type) => {
      const count = source[type];
      if (partial && type === "persona") {
        if (count !== null) {
          throw new Error("Invalid archive response: counts.persona");
        }
        return [type, null];
      }
      if (!Number.isInteger(count) || (count as number) < 0) {
        throw new Error(`Invalid archive response: counts.${type}`);
      }
      return [type, count];
    }),
  ) as ArchiveCounts;
}

function parseArchivePage(value: unknown): ArchivePage {
  const source = requireRecord(value, "response");
  if (source.contract_version !== 1) {
    throw new Error("Invalid archive response: contract_version");
  }
  const snapshotAt = requireString(source.snapshot_at, "snapshot_at");
  if (!Number.isFinite(Date.parse(snapshotAt))) {
    throw new Error("Invalid archive response: snapshot_at");
  }

  const partial = source.partial === true;
  const excludedTypes = partial
    ? (() => {
        if (
          !Array.isArray(source.excluded_types) ||
          source.excluded_types.length !== 1 ||
          source.excluded_types[0] !== "persona"
        ) {
          throw new Error("Invalid archive response: excluded_types");
        }
        return ["persona"] as ArchiveType[];
      })()
    : [];
  const resourceVersions = parseResourceVersions(source.resource_versions, partial);
  if (partial ? resourceVersions.persona.enabled : !resourceVersions.persona.enabled) {
    throw new Error("Invalid archive response: resource_versions.persona");
  }

  if (!Array.isArray(source.items)) {
    throw new Error("Invalid archive response: items");
  }
  const items = source.items.map(parseItem);
  if (partial && items.some((item) => item.type === "persona")) {
    throw new Error("Invalid archive response: items.persona");
  }

  const page = requireRecord(source.page, "page");
  if (!Number.isInteger(page.limit) || (page.limit as number) < 1 || (page.limit as number) > 100) {
    throw new Error("Invalid archive response: page.limit");
  }
  if (typeof page.has_more !== "boolean") {
    throw new Error("Invalid archive response: page.has_more");
  }
  if (page.next_cursor !== null && typeof page.next_cursor !== "string") {
    throw new Error("Invalid archive response: page.next_cursor");
  }
  if (page.has_more && !page.next_cursor) {
    throw new Error("Invalid archive response: page.next_cursor");
  }

  return {
    contractVersion: 1,
    snapshotAt,
    resourceVersions,
    counts: parseCounts(source.counts, partial),
    items,
    page: {
      limit: page.limit as number,
      nextCursor: page.next_cursor as string | null,
      hasMore: page.has_more,
    },
    partial,
    excludedTypes,
  };
}

async function errorFromResponse(response: Response): Promise<ArchiveApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // The status still gives the caller a fail-visible error.
  }

  const detail = isRecord(body) && isRecord(body.detail) ? body.detail : body;
  const code = isRecord(detail) && typeof detail.code === "string" ? detail.code : null;
  const message =
    (isRecord(detail) && typeof detail.message === "string" && detail.message) ||
    (typeof body === "string" && body) ||
    `Archive request failed (${response.status})`;
  return new ArchiveApiError(response.status, message, code);
}

export function isArchivePageNotFound(error: unknown): error is ArchiveApiError {
  return (
    error instanceof ArchiveApiError &&
    error.status === 404 &&
    error.code === "archive_page_not_found"
  );
}

export async function fetchArchivePage(
  request: ArchivePageRequest = {},
): Promise<ArchivePage> {
  const params = new URLSearchParams({ contract_version: "1" });
  if (request.type) params.set("type", request.type);
  if (request.q) params.set("q", request.q);
  if (request.cursor) params.set("cursor", request.cursor);
  if (request.limit !== undefined) params.set("limit", String(request.limit));
  if (request.knowledge) params.set("knowledge", request.knowledge);

  const response = await apiFetch(apiUrl(`/api/v1/archive?${params.toString()}`), {
    cache: "no-store",
  });
  if (!response.ok) throw await errorFromResponse(response);
  return parseArchivePage(await response.json());
}
